import { getTextObjValue } from '@team-milva/lab-models/textObj'
import type { ITextList } from '@team-milva/lab-models/resources'

export type TResultTextOption = { value: string; text: string }

// A picker over the shared `result-texts` Labels list. Four surfaces
// render one (the two prove-based label pickers, plus the value-based
// condition label picker on both the focus flow and the global list page), so
// the option rule lives here rather than being re-derived in each of them.
//
// The rule (KB, 2026-09-03) is the same one `unitID` and `reportDescriptionIDs`
// already follow: list the active items, plus whichever item is currently
// selected even after it has been deactivated. Without the keep-selected
// clause a saved value silently vanishes from its own dropdown. Once the user
// picks something else the inactive item is gone and cannot be chosen again.
export function buildResultTextItems({
  list,
  locale,
  selectedIDs,
  pendingItems,
}: {
  list: ITextList | undefined
  locale: string
  selectedIDs: (string | null | undefined)[]
  // Items typed into a picker but not yet committed to the library — staged
  // per surface, so each caller passes its own.
  pendingItems: TResultTextOption[]
}): TResultTextOption[] {
  const kept = new Set(selectedIDs.filter((id): id is string => !!id))

  const storeItems = !list
    ? []
    : Object.entries(list.items)
        .filter(([id, item]) => item.activeState === 'active' || kept.has(id))
        .map(([id, item]) => ({ value: id, text: getTextObjValue(item.text, locale) }))
        .sort((a, b) => a.text.localeCompare(b.text))

  return [...storeItems, ...pendingItems]
}
